import { useRef, useState } from 'react'
import { Grid, TextField, Button } from '@mui/material'

import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSnackbar } from 'hooks'

import { MUI, PasswordValidator, StandardPageStructure } from 'components'
import { useNavigate } from 'react-router-dom'

import ReCAPTCHA from 'react-google-recaptcha'

import { formatters } from 'helpers'
import service from 'service'
import { getGoogleRecaptchaToken } from 'service/env'

import routes from 'constants/routes'

import schema from './schema'

const TemporaryPassword = () => {
  const [isLoading, setLoading] = useState<boolean>(false)
  const [recaptcha, setRecaptcha] = useState<string | null>()
  const recaptchaRef = useRef<ReCAPTCHA | null>(null)

  const { snackbar } = useSnackbar()

  const navigate = useNavigate()

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  const onSubmit = async (data: object) => {
    try {
      setLoading(true)
      const response = await service.dponet.auth.changeTemporaryPassword(data)
      setLoading(false)
      snackbar.open({
        message:
          response?.data?.message || 'Sua senha foi alterada com sucesso.',
        variant: 'success',
      })
      service.dponet.auth.logout()
      navigate(routes.auth.login)
    } catch (error) {
      console.error(error)
      setLoading(false)
      snackbar.open({
        message: formatters.errorMessage(error),
        variant: 'error',
      })
    }
  }

  const expiredRecaptcha = () => {
    setValue('recaptcha', '')
    setRecaptcha(null)
  }

  const handleRecaptcha = (token: string | null) => {
    if (token) {
      setValue('recaptcha', token)
      setRecaptcha(token)
    }
  }

  return (
    <StandardPageStructure display="flex" justifyContent="center">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MUI.Typography variant="h4" color="textPrimary">
              Cadastre sua nova senha!
            </MUI.Typography>
            <MUI.Typography variant="subtitle1" color="textPrimary">
              Você utilizou uma senha temporária, cadastre uma nova senha para
              continuar.
            </MUI.Typography>
          </Grid>
          <Grid item xs={12}>
            <PasswordValidator
              passwordInput={watch('newPassword')}
              justSubmitted={isSubmitted}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!errors.newPassword}
                  helperText={<>{errors.newPassword?.message}</>}
                  label="Nova senha"
                  type="password"
                  autoComplete="off"
                  placeholder="Digite sua nova senha"
                  fullWidth
                />
              )}
              name="newPassword"
              control={control}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!errors.confirmPassword}
                  helperText={<>{errors.confirmPassword?.message}</>}
                  label="Confirmar nova senha"
                  type="password"
                  autoComplete="off"
                  placeholder="Confirme sua nova senha"
                  fullWidth
                />
              )}
              name="confirmPassword"
              control={control}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              render={({ field }) => (
                <ReCAPTCHA
                  {...field}
                  ref={recaptchaRef}
                  sitekey={getGoogleRecaptchaToken()}
                  onChange={(token) => handleRecaptcha(token)}
                  onExpired={expiredRecaptcha}
                  size="normal"
                />
              )}
              name="recaptcha"
              control={control}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!recaptcha || isLoading}
            >
              {isLoading ? 'Carregando...' : 'Cadastrar'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </StandardPageStructure>
  )
}

export default TemporaryPassword
